'use client';

// App-wide single source of truth for the unread-notifications count.
//
// Why this exists: the count was previously computed independently in ~8 places
// (the bell fetched once on mount and never refreshed; the navbar polled
// /api/notifications every 30s; the follower badge rolled its own realtime).
// This provider mounts once at the app root (alongside DmRealtimeProvider) and
// exposes ONE count that every badge consumes, kept live via a realtime
// subscription on the notifications table.
//
// Realtime respects RLS: the notifications SELECT policy is user_id = auth.uid(),
// and the subscription is additionally filtered to user_id=eq.{me}. On any
// change we recompute the count with a head/count query rather than doing
// arithmetic off the payload — simpler and immune to missed/duplicate events.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { useAuthContext } from '@/components/providers/AuthProvider';
import { createClient } from '@/lib/supabase/browser';

interface NotificationsContextValue {
  /** Number of unread notifications for the current user. */
  unreadCount: number;
  /** Recompute the count now — call after mutating read state (mark-read/all). */
  refresh: () => void;
  /**
   * Subscribe to notifications-table realtime changes for the current user
   * (the same signal that drives the count). Lets a surface like the
   * /notifications list re-fetch live when a notification is inserted, read, or
   * deleted, without opening its own duplicate DB subscription. Returns an
   * unsubscribe fn.
   */
  onChange: (listener: () => void) => () => void;
}

const NotificationsContext = createContext<NotificationsContextValue | undefined>(undefined);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthContext();
  const supabase = useMemo(() => createClient(), []);
  const [unreadCount, setUnreadCount] = useState(0);

  const userId = user?.id ?? null;
  const userIdRef = useRef<string | null>(null);
  userIdRef.current = userId;

  const listenersRef = useRef<Set<() => void>>(new Set());
  const onChange = useCallback((listener: () => void) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const recount = useCallback(async () => {
    const id = userIdRef.current;
    if (!id) {
      setUnreadCount(0);
      return;
    }
    const { count } = await supabase
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', id)
      .eq('read', false);
    // Guard against a late response after the user changed.
    if (userIdRef.current === id) setUnreadCount(count ?? 0);
  }, [supabase]);

  useEffect(() => {
    if (!userId) {
      setUnreadCount(0);
      return;
    }

    void recount();

    const channel: RealtimeChannel = supabase
      .channel(`notifications-${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        () => {
          void recount();
          // Fan the same signal out to subscribed surfaces (e.g. the
          // /notifications list) so they can refresh live.
          listenersRef.current.forEach((listener) => {
            try {
              listener();
            } catch {
              // A bad listener must not break delivery for the others.
            }
          });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [userId, supabase, recount]);

  // `refresh` must be `recount` itself, not a fresh arrow each render: consumers
  // put it in useEffect deps (the post-detail mark-post-read effect does), and a
  // new identity on every unreadCount change would re-fire those effects on
  // every notification that arrives.
  const value = useMemo<NotificationsContextValue>(
    () => ({ unreadCount, refresh: recount, onChange }),
    [unreadCount, recount, onChange],
  );

  return (
    <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>
  );
}

export function useUnreadNotifications(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (ctx === undefined) {
    throw new Error('useUnreadNotifications must be used within a NotificationsProvider');
  }
  return ctx;
}
